/**
 * Composite Alert detail presenter. Server operational Composite fields are authority.
 * Does not invent Immediate/Pattern classification, Control/Since/Scan, or same-person claims.
 */
import { FORKLIFT_PEDESTRIAN_LABEL } from '../buyerFacing/conditionDisplayNames';
import { emailDeliveryBuyerCopy } from './liveAlertsReconciliation';
import type { BuyerLiveItem, BuyerCompositeTerm } from './liveAlertsReconciliation';
import { historyDeliveryLabel, historyDeliveryState } from './alertHistoryPresentation';

export type CompositeDetailTerm = {
  termId: string;
  conditionType: string;
  conditionLabel: string;
  sourcePolicyName: string | null;
  requiredCount: number | null;
  observedCount: number | null;
  membership: Array<{
    eventId: string;
    occurredAt: string | null;
    policyId: string | null;
    policyName: string | null;
    policyVersion: string | null;
  }>;
};

export type CompositeAlertDetailView = {
  incidentId: string;
  alertClass: 'Composite';
  ruleName: string;
  operator: string;
  readableSentence: string;
  location: string;
  windowLabel: string;
  scopeLabel: string;
  cooldownLabel: string;
  triggeredAt: string;
  severity: string;
  deliveryLabel: string;
  deliveryCopy: string;
  terms: CompositeDetailTerm[];
  ruleId: string;
  ruleVersion: string;
  evidencePresent: boolean;
};

const CONTROL_PLANE_TOKEN = /SHADOW_COMPARE|EXPERIMENTAL_SHADOW/gi;

function text(value: unknown): string {
  return String(value || '').trim();
}

function customerSafe(value: string): string {
  return text(value.replace(CONTROL_PLANE_TOKEN, '')).replace(/\s{2,}/g, ' ');
}

function formatWindow(seconds: unknown): string {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '—';
  if (value % 3600 === 0) {
    const hours = value / 3600;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (value % 60 === 0) {
    const minutes = value / 60;
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  return `${value} seconds`;
}

function formatCooldown(seconds: unknown): string {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value === 0) return 'None';
  return formatWindow(value);
}

function conditionLabel(conditionType: string, provided?: string | null): string {
  if (text(provided)) return customerSafe(text(provided));
  if (conditionType === 'RESTRICTED_AREA_ENTRY') return 'Restricted-area entry';
  if (conditionType === 'PPE_NON_COMPLIANCE') return 'PPE non-compliance';
  if (conditionType === 'PERSON_FORKLIFT_PROXIMITY') return FORKLIFT_PEDESTRIAN_LABEL;
  return customerSafe(conditionType.replace(/_/g, ' '));
}

function participatingTerms(item: BuyerLiveItem): BuyerCompositeTerm[] {
  const op = item.operational;
  const operator = text(op.composite_operator).toUpperCase();
  const terms = Array.isArray(op.composite_terms) ? op.composite_terms : [];
  return terms.filter((term) => {
    const required = Number(term.required_count);
    const observed = Number(term.observed_count);
    if (!Number.isFinite(required) || !Number.isFinite(observed) || observed < required) return false;
    if (operator === 'ANY') return observed >= required;
    return true;
  });
}

export function presentCompositeAlertDetail(item: BuyerLiveItem | null | undefined): CompositeAlertDetailView | null {
  const op = item?.operational;
  if (!op) return null;
  if (op.excluded === true) return null;
  const incidentId = text(op.incident_id);
  if (!incidentId) return null;
  const alertClass = text(op.alert_class);
  if (
    alertClass === 'Immediate'
    || alertClass === 'Pattern'
    || text(op.event_kind) === 'PATTERN_ALERT'
    || text(op.event_kind) === 'CONDITION_EVENT'
    || text(op.trigger_kind) === 'TEMPORAL_PATTERN'
  ) {
    return null;
  }
  if (
    alertClass !== 'Composite'
    && text(op.event_kind) !== 'COMPOSITE_ALERT'
    && text(op.trigger_kind) !== 'COMPOSITE'
    && text(op.incident_type) !== 'composite_alert'
    && !incidentId.startsWith('ci1_')
  ) {
    return null;
  }
  if (!text(op.composite_rule_id) || !text(String(op.composite_rule_version ?? '')) || !text(op.composite_operator)) {
    return null;
  }
  const terms = participatingTerms(item);
  const deliveryState = historyDeliveryState(op.notification_delivery);
  const severityRaw = op.severity && typeof op.severity === 'object' ? op.severity.severity : op.severity;
  const locationRaw = customerSafe(text(op.composite_location_display));
  const locationToken = locationRaw.toLowerCase();
  const location = !locationRaw || locationToken === 'worksafety' || locationToken === 'n/a' || locationToken === 'unknown'
    ? 'configured area'
    : locationRaw;
  return {
    incidentId,
    alertClass: 'Composite',
    ruleName: customerSafe(text(op.composite_rule_name) || 'Composite Alert'),
    operator: text(op.composite_operator).toUpperCase(),
    readableSentence: customerSafe(text(op.composite_readable_sentence)),
    location,
    windowLabel: formatWindow(op.composite_window_seconds),
    scopeLabel: customerSafe(text(op.composite_scope_label) || text(op.composite_scope_binding) || '—'),
    cooldownLabel: formatCooldown(op.composite_cooldown_seconds),
    triggeredAt: text(op.composite_triggered_at) || text(op.recorded_at) || '—',
    severity: customerSafe(text(severityRaw) || '—'),
    deliveryLabel: historyDeliveryLabel(deliveryState),
    deliveryCopy: customerSafe(emailDeliveryBuyerCopy(op.notification_delivery)),
    terms: terms.map((term) => ({
      termId: text(term.term_id) || text(term.condition_type),
      conditionType: text(term.condition_type),
      conditionLabel: conditionLabel(text(term.condition_type), term.condition_label),
      sourcePolicyName: customerSafe(text(term.source_policy_name))
        || customerSafe(text((term.membership || []).find((member) => text(member.policy_name))?.policy_name))
        || null,
      requiredCount: term.required_count == null ? null : Number(term.required_count),
      observedCount: term.observed_count == null ? null : Number(term.observed_count),
      membership: (term.membership || []).map((member) => ({
        eventId: text(member.event_id),
        occurredAt: text(member.occurred_at) || null,
        policyId: text(member.policy_id) || null,
        policyName: customerSafe(text(member.policy_name)) || null,
        policyVersion: member.policy_version == null ? null : String(member.policy_version),
      })).filter((member) => member.eventId || member.occurredAt || member.policyName),
    })),
    ruleId: customerSafe(text(op.composite_rule_id)),
    ruleVersion: customerSafe(String(op.composite_rule_version ?? '')),
    evidencePresent: op.evidence_present === true,
  };
}

export type ReviewedCompositeOccurrence = {
  incidentId: string;
  occurredAt: string | null;
  location: string;
  terms: CompositeDetailTerm[];
};

export type ReviewedCompositePatternView = {
  subjectKind: 'COMPOSITE';
  ruleName: string;
  headline: string;
  location: string;
  evidenceCopy: string;
  occurrenceCount: number;
  members: ReviewedCompositeOccurrence[];
  patternIncidentId: string;
  controlPath: string | null;
};

function buyerLocationFromOp(op: BuyerLiveItem['operational']): string {
  const locationRaw = customerSafe(text(op.composite_location_display));
  const locationToken = locationRaw.toLowerCase();
  if (!locationRaw || locationToken === 'worksafety' || INTERNAL_LOCATION.has(locationToken) || locationToken === 'n/a' || locationToken === 'unknown') {
    return '';
  }
  return locationRaw;
}

const INTERNAL_LOCATION = new Set([
  'worksafety', 'n/a', 'unknown', 'none', 'null', '-', 'shadow_compare', 'experimental_shadow',
]);

export function presentReviewedCompositePattern(
  item: BuyerLiveItem | null | undefined,
  members: BuyerLiveItem[] = [],
): ReviewedCompositePatternView | null {
  const op = item?.operational;
  if (!op) return null;
  if (text(op.trigger_kind) !== 'TEMPORAL_PATTERN') return null;
  if (text(op.event_kind) === 'PATTERN_ALERT') return null;
  if (text(op.subject_kind) !== 'COMPOSITE' && text(op.subject_type) !== 'COMPOSITE') return null;
  const ruleName = customerSafe(text(op.composite_rule_name) || 'Composite');
  const ids = (op.pattern_membership?.source_occurrence_ids || []).map((id) => text(id)).filter(Boolean);
  const memberById = new Map(members.map((member) => [text(member.operational.incident_id), member]));
  const occurrences: ReviewedCompositeOccurrence[] = [];
  const locations: string[] = [];
  for (const id of ids) {
    const member = memberById.get(id);
    const detail = member ? presentCompositeAlertDetail(member) : null;
    const location = member ? buyerLocationFromOp(member.operational) : '';
    if (location) locations.push(location);
    occurrences.push({
      incidentId: id,
      occurredAt: detail?.triggeredAt || text(member?.operational.recorded_at) || null,
      location: location || 'configured area',
      terms: detail?.terms || [],
    });
  }
  const unique = Array.from(new Set(locations));
  const location = unique.length === 1 ? unique[0] : 'configured area';
  const evidenceNames = Array.from(new Set(
    occurrences.flatMap((occ) => occ.terms.map((term) => term.conditionLabel).filter(Boolean)),
  ));
  const relationship = evidenceNames.length >= 2
    ? `${evidenceNames[0]} and ${evidenceNames[1]}`
    : 'the configured relationship';
  const siteId = text(op.site_id);
  const ruleId = text(op.composite_rule_id);
  const scopeType = text(op.composite_evaluated_scope_type) || 'camera';
  const scopeId = text(op.composite_evaluated_scope_id) || text(op.camera_id);
  const controlPath = siteId && ruleId && scopeId
    ? `/acceptance/lifecycle?site_id=${encodeURIComponent(siteId)}&subject_kind=COMPOSITE&composite_rule_id=${encodeURIComponent(ruleId)}&composite_scope_type=${encodeURIComponent(scopeType)}&composite_scope_id=${encodeURIComponent(scopeId)}&focus=control`
    : null;
  return {
    subjectKind: 'COMPOSITE',
    ruleName,
    headline: `${ruleName} keeps occurring`,
    location,
    evidenceCopy: `Multiple occurrences of the configured relationship between ${relationship}.`,
    occurrenceCount: ids.length || Number(op.pattern_membership?.observed_count || 0),
    members: occurrences,
    patternIncidentId: text(op.incident_id),
    controlPath,
  };
}
